import { ReactComponent as EmptyPanelDroneIcon } from '@assets/components/panel/notification/empty-panel-drone-icon.svg';
import { Center, For, Text, useBreakpointValue, VStack } from '@chakra-ui/react';
import { DraggablePanel } from '@components/ui/draggable';
import { useSelectorWithReplayMode } from '@hooks/useSelectorWithReplayMode';
import { selectSituationFlightPlans } from '@redux/situation/situation.selectors';
import { sortDate } from '@utils/date.utils';
import { FormattedMessage } from 'react-intl';

import { PopupPositionData } from '@/types/popup.types';

import FlightPlanCard from './FlightPlanCard';

export default function DailyFlightPlans({ mapConstraintsRef, top, left, onClose }: Readonly<PopupPositionData>) {
  const flightPlans = useSelectorWithReplayMode(selectSituationFlightPlans);
  const activations = flightPlans.flatMap((flightPlan) =>
    flightPlan.activations.map((activation) => ({ flightPlan, activation })),
  );
  const bodyMaxHeight = useBreakpointValue({ base: 530, '4k': 612 });
  return (
    <DraggablePanel.Root constraintsRef={mapConstraintsRef} top={top} left={left}>
      <DraggablePanel.Header onClose={onClose}>
        <FormattedMessage id="dailyFlightPlans.title" />
      </DraggablePanel.Header>
      <DraggablePanel.Body maxHeight={bodyMaxHeight} minHeight={375} width={{ base: '452px', '4k': '520px' }}>
        <VStack gap={0.25} flex={1} overflow="hidden">
          <Text
            paddingX={4}
            paddingY={3}
            backgroundColor="neutral.700"
            textStyle="screenStd"
            fontWeight="medium"
            width="100%"
          >
            <FormattedMessage id="dailyFlightPlans.description" values={{ count: activations.length }} />
          </Text>
          <VStack flex={1} width="100%" gap={0.25} overflow="hidden" overflowY="auto" className="map-panel-scrollbar">
            <For
              each={activations.toSorted(({ activation: a1 }, { activation: a2 }) =>
                sortDate(a2.startDate, a1.startDate),
              )}
              fallback={
                <Center width="100%" flex={1}>
                  <VStack width="100%">
                    <EmptyPanelDroneIcon />
                    <Text textStyle="screenStd" color="neutral.200">
                      <FormattedMessage id="dailyFlightPlans.empty" />
                    </Text>
                  </VStack>
                </Center>
              }
            >
              {(info) => (
                <FlightPlanCard
                  flightPlan={info.flightPlan}
                  activation={info.activation}
                  position={{ x: left, y: top }}
                  key={info.activation.id}
                  onClose={onClose}
                />
              )}
            </For>
          </VStack>
        </VStack>
      </DraggablePanel.Body>
    </DraggablePanel.Root>
  );
}
